<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { Button, FormControl, Modal, Option, Select, Textarea, type SelectOption } from '@ui'
import type { Colleague } from '@/api/intelligence-actions'
import { useAccessibleTagsEmptyLabel } from '@/ui-delta/useAccessibleTagsEmptyLabel'

export interface SharePayload {
  recipientIds: string[]
  note?: string
}

/**
 * Share with note (S0 §35b, P6 §2.1): an FYI to one or more colleagues. It
 * creates no follow-up and asks nothing of them, and the form says so; the
 * one sharing it is not among the people offered.
 */
const props = withDefaults(defineProps<{ open: boolean; colleagues: Colleague[]; context: string; meId: string; pending?: boolean; colleaguesFailed?: boolean }>(), {
  pending: false,
  colleaguesFailed: false,
})
const emit = defineEmits<{ close: []; submit: [share: SharePayload] }>()
const { t } = useI18n()

const recipients = ref<string[]>([])
const note = ref('')

watch(
  () => props.open,
  (open) => {
    if (!open) return
    recipients.value = []
    note.value = ''
  },
  { immediate: true },
)

const people = computed<SelectOption[]>(() =>
  props.colleagues
    .filter((colleague) => colleague.userId !== props.meId)
    .map((colleague) => ({
      value: colleague.userId,
      label: colleague.name,
      ...(colleague.hasIntelligence ? {} : { description: t('actions.form.noSeatYet') }),
    })),
)
const chosen = computed(() => people.value.filter((option) => recipients.value.includes(option.value)))
const choose = (options: SelectOption | SelectOption[] | undefined): void => {
  recipients.value = (Array.isArray(options) ? options : options ? [options] : []).map((option) => option.value)
}

/** The kit paints its own "No options" in an empty multiple Select: this field says what to do instead (ui-delta/theme.css). */
const recipientsRoot = ref<HTMLElement | null>(null)
const emptyLabel = (): string => t('actions.form.choosePeople')
const emptyLabelStyle = computed(() => ({ '--jf-empty-label': JSON.stringify(emptyLabel()) }))
useAccessibleTagsEmptyLabel(recipientsRoot, emptyLabel)

const canSubmit = computed(() => recipients.value.length > 0)

const submit = (): void => {
  if (!canSubmit.value) return
  const text = note.value.trim()
  emit('submit', { recipientIds: [...recipients.value], ...(text ? { note: text } : {}) })
}
</script>

<template>
  <Modal :model-value="open" :title="t('actions.share.title')" size="md" type="content" no-icon @close="emit('close')">
    <template #content>
      <div class="form" data-test="share-form">
        <p class="form__context">{{ t('actions.share.what', { name: context }) }}</p>
        <p class="form__rule" data-test="share-rule">{{ t('actions.share.rule') }}</p>
        <FormControl
          name="share-recipients"
          :label="t('actions.share.recipients')"
          :error-message="colleaguesFailed ? t('actions.form.colleaguesFailed') : undefined"
          required
        >
          <div ref="recipientsRoot" :style="emptyLabelStyle">
          <Select
            :model-value="chosen"
            :options="people"
            multiple
            searchable
            :placeholder="t('actions.form.choosePeople')"
            data-test="share-recipients"
            @update:model-value="choose"
          >
            <Option v-for="option in people" :key="option.value" :label="option.label" :value="option.value" :description="option.description" />
          </Select>
          </div>
        </FormControl>
        <FormControl name="share-note" :label="t('actions.share.note')" :help-text="t('actions.share.noteHelp')">
          <Textarea v-model="note" :maxlength="2000" data-test="share-note" />
        </FormControl>
      </div>
    </template>

    <template #actions>
      <Button type="secondary" @click="emit('close')">{{ t('common.cancel') }}</Button>
      <Button :loading="pending" :disabled="!canSubmit" :aria-disabled="!canSubmit" data-test="share-submit" @click="submit">
        {{ t('actions.share.submit') }}
      </Button>
    </template>
  </Modal>
</template>

<style scoped>
.form {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xl);
}

.form__context {
  margin: 0;
  font-size: var(--font-size-body-2);
  line-height: var(--line-height-body-2);
  color: var(--fg-primary);
}

.form__rule {
  margin: 0;
  padding: var(--spacing-sm) var(--spacing-md);
  border-radius: var(--radius-lg);
  background: var(--surface-sunken);
  font-size: var(--font-size-body-3);
  line-height: var(--line-height-body-3);
  color: var(--fg-secondary);
}
</style>
